import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Search, RefreshCw, Users, LogIn, ShieldAlert, LayoutGrid } from 'lucide-react';
import { aplicacionesAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import LogoApp from '../components/LogoApp';
import { haceCuanto } from '../utils/tiempo';

const Kpi = ({ icono: Icono, etiqueta, valor, detalle, tono = 'normal' }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3">
    <div className={`p-2.5 rounded-lg shrink-0 ${
      tono === 'alerta' ? 'bg-red-50 dark:bg-red-900/20 text-red-600' : tono === 'aviso' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600'
    }`}>
      <Icono className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400">{etiqueta}</p>
      <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{valor}</p>
      {detalle && <p className="text-[11px] text-gray-400 truncate">{detalle}</p>}
    </div>
  </div>
);

export default function Aplicaciones() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [soloAlertas, setSoloAlertas] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await aplicacionesAPI.getResumen();
      setDatos(data);
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudieron cargar las aplicaciones', 'error');
    } finally {
      setCargando(false);
    }
  }, [showToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const apps = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (datos?.apps || []).filter((a) => {
      if (soloAlertas && !(a.cuentasInactivasConAcceso > 0 || a.fallidos30d > 0)) return false;
      return !q || `${a.nombre} ${a.codigo} ${a.descripcion || ''}`.toLowerCase().includes(q);
    });
  }, [datos, busqueda, soloAlertas]);

  const t = datos?.totales;

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Aplicaciones</h1>
          <p className="text-gray-600 dark:text-gray-400">Control de las aplicaciones del holding: usuarios, accesos y permisos</p>
        </div>
        <button
          type="button"
          onClick={cargar}
          disabled={cargando}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${cargando ? 'animate-spin' : ''}`} />Actualizar
        </button>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Kpi icono={LayoutGrid} etiqueta="Aplicaciones activas" valor={`${t.appsActivas} / ${t.apps}`} />
          <Kpi icono={Users} etiqueta="Personas con acceso" valor={t.usuariosConAcceso} detalle="En al menos una app" />
          <Kpi icono={LogIn} etiqueta="Ingresos hoy" valor={t.accesosHoy} />
          <Kpi icono={LogIn} etiqueta="Ingresos 7 días" valor={t.accesos7d} />
          <Kpi icono={AlertTriangle} etiqueta="Ingresos fallidos (30 d)" valor={t.fallidos30d} tono={t.fallidos30d > 0 ? 'aviso' : 'normal'} />
          <Kpi icono={ShieldAlert} etiqueta="Cuentas inactivas con acceso" valor={t.cuentasInactivasConAcceso} tono={t.cuentasInactivasConAcceso > 0 ? 'alerta' : 'normal'} detalle={t.cuentasInactivasConAcceso > 0 ? 'Conviene revocarlas' : 'Todo en orden'} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar aplicación…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
          <input type="checkbox" checked={soloAlertas} onChange={(e) => setSoloAlertas(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          Solo con alertas
        </label>
      </div>

      {cargando && !datos ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
      ) : apps.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 text-sm text-gray-500">No hay aplicaciones que coincidan.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {apps.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => navigate(`/aplicaciones/${a.codigo}`)}
              className="text-left flex flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <div className="flex items-start justify-between gap-3">
                <LogoApp codigo={a.codigo} activa={a.activa} />
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${a.activa ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                  {a.activa ? 'Activa' : 'Inactiva'}
                </span>
              </div>
              <h3 className="mt-3 text-base font-bold text-gray-900 dark:text-white">{a.nombre}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2 min-h-[2.5rem]">{a.descripcion || 'Aplicación integrada del ecosistema'}</p>

              <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 py-2">
                  <dd className="text-lg font-bold text-gray-900 dark:text-white">{a.usuariosConAcceso}</dd>
                  <dt className="text-[10px] uppercase tracking-wide text-gray-400">Usuarios</dt>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 py-2">
                  <dd className="text-lg font-bold text-gray-900 dark:text-white">{a.accesos7d}</dd>
                  <dt className="text-[10px] uppercase tracking-wide text-gray-400">Ingresos 7 d</dt>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 py-2">
                  <dd className="text-lg font-bold text-gray-900 dark:text-white">{a.accesos30d}</dd>
                  <dt className="text-[10px] uppercase tracking-wide text-gray-400">Ingresos 30 d</dt>
                </div>
              </dl>

              <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-gray-500 dark:text-gray-400">Último ingreso: <b className="text-gray-700 dark:text-gray-200">{a.ultimoAcceso ? haceCuanto(a.ultimoAcceso) : 'sin registros'}</b></span>
              </div>
              {(a.cuentasInactivasConAcceso > 0 || a.fallidos30d > 0) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {a.cuentasInactivasConAcceso > 0 && <span className="px-2 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-[11px] font-semibold">{a.cuentasInactivasConAcceso} cuenta(s) inactiva(s) con acceso</span>}
                  {a.fallidos30d > 0 && <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-[11px] font-semibold">{a.fallidos30d} fallido(s)</span>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
